import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { apiClient } from "@/services/api";

const AGENT_COUNT_OPTIONS = [
  "1-5",
  "6-20",
  "21-50",
  "51-100",
  "100+",
];

interface RequestAccessModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function RequestAccessModal({ open, onOpenChange, onSuccess }: RequestAccessModalProps) {
  const [companyName, setCompanyName] = useState("");
  const [agentCount, setAgentCount] = useState("");
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (data: { company_name: string; agent_count: string }) =>
      apiClient.post("/api/v1/access-requests", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access-request-me"] });
      onSuccess();
      onOpenChange(false);
    },
    onError: (error: any) => {
      // If duplicate (409), treat as success — they already requested
      if (error?.status === 409 || error?.response?.status === 409) {
        onSuccess();
        onOpenChange(false);
      }
    },
  });

  const canSubmit = companyName.trim().length > 0 && agentCount.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Request Access to Real-time Monitoring</DialogTitle>
          <DialogDescription>
            Get early access to auto-detect prompt injection, jailbreaks, and data leakage as they happen.
          </DialogDescription>
        </DialogHeader>
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit && !mutation.isPending) {
              mutation.mutate({ company_name: companyName.trim(), agent_count: agentCount });
            }
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="company_name">Company name</Label>
            <Input
              id="company_name"
              placeholder="Acme Inc."
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              maxLength={255}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="agent_count">How many AI agents are you running?</Label>
            <Select value={agentCount} onValueChange={setAgentCount}>
              <SelectTrigger id="agent_count">
                <SelectValue placeholder="Select a range" />
              </SelectTrigger>
              <SelectContent>
                {AGENT_COUNT_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="submit" className="w-full" disabled={!canSubmit || mutation.isPending}>
            {mutation.isPending ? "Submitting..." : "Request Access"}
          </Button>
          {mutation.isError && (
            <p className="text-sm text-destructive text-center">
              {(mutation.error as any)?.message?.includes("already")
                ? "You've already requested access."
                : "Something went wrong. Please try again."}
            </p>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
}
